import React, { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Camera, StopCircle } from 'lucide-react'

interface EnvCheckModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: (blob: Blob) => void
  checkNumber: number
}

const EnvCheckModal = ({ isOpen, onClose, onComplete, checkNumber }: EnvCheckModalProps) => {
  const [isRecording, setIsRecording] = useState(false)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const chunksRef = useRef<Blob[]>([])

  // Start recording when modal opens
  useEffect(() => {
    if (isOpen) {
      startRecording()
    } else {
      stopStream()
    }
    return () => stopStream()
  }, [isOpen])

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      })

      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm',
        videoBitsPerSecond: 500000,
      })

      mediaRecorderRef.current = mediaRecorder
      chunksRef.current = []

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'video/webm' })
        onComplete(blob)
        stopStream()
      }

      mediaRecorder.start()
      setIsRecording(true)
    } catch (error) {
      console.error('Error accessing camera:', error)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }

  const handleClose = () => {
    stopRecording()
    stopStream()
    setIsRecording(false)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle> 360° Çevre Kontrolü #{checkNumber}</DialogTitle>
          <DialogDescription>
            Laptop veya cihazınız ile bulunduğunuz ortamı yerinizden kalkmadan 360° derece çevrenizi
            gösterin. Kaydı bitirmek için "Kaydı Tamamla" butonuna tıklayınız.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            {isRecording && (
              <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm">
                Kayıt Ediliyor...
              </div>
            )}
          </div>

          <div className="flex justify-center gap-4">
            {isRecording && (
              <Button
                onClick={stopRecording}
                className="flex items-center gap-2"
                variant="destructive"
              >
                <StopCircle className="h-4 w-4" />
                Kaydı Tamamla
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default EnvCheckModal
